import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Typography, Button, IconButton, Tooltip,
  Table, TableBody, TableCell, TableHead, TableRow,
  Chip, TextField, InputAdornment, Dialog, DialogTitle,
  DialogContent, DialogActions, List, ListItem, ListItemText,
  ListItemSecondaryAction, CircularProgress,
} from "@material-ui/core";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import AddIcon from "@material-ui/icons/Add";
import EditIcon from "@material-ui/icons/Edit";
import DeleteIcon from "@material-ui/icons/Delete";
import SearchIcon from "@material-ui/icons/Search";
import CategoryIcon from "@material-ui/icons/Category";
import CheckIcon from "@material-ui/icons/Check";
import CloseIcon from "@material-ui/icons/Close";
import { toast } from "react-toastify";

import TableRowSkeleton from "../../components/TableRowSkeleton";
import AppMarketplaceModal from "../../components/AppMarketplaceModal";
import {
  listMarketplaceAdmin, deleteMarketplaceItem,
  listCategories, createCategory, updateCategory, deleteCategory,
} from "../../services/appMarketplaceService";
import { getBackendUrl } from "../../config";

const backendUrl = getBackendUrl();

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(4),
    width: "100%",
    background: "#f8fafc",
    minHeight: "100vh",
  },
  pageHeader: {
    marginBottom: theme.spacing(3),
  },
  pageTitle: {
    fontSize: "28px",
    fontWeight: 700,
    color: "#1a1a2e",
    marginBottom: theme.spacing(0.5),
  },
  pageSubtitle: {
    fontSize: "14px",
    color: "#6b7280",
  },
  toolbarRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    background: "#ffffff",
    borderRadius: 12,
    padding: theme.spacing(1, 2),
    marginBottom: theme.spacing(3),
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.04)",
    flexWrap: "wrap",
    gap: 12,
  },
  searchField: {
    backgroundColor: "#f8fafc",
    borderRadius: 8,
    minWidth: 240,
    "& .MuiOutlinedInput-root": {
      borderRadius: 8,
      "& fieldset": { borderColor: "#e0e0e0" },
      "&:hover fieldset": { borderColor: theme.palette.primary.main },
    },
  },
  addButton: { borderRadius: 8, padding: "6px 20px", textTransform: "none", fontWeight: 600 },
  content: {
    background: "#ffffff",
    borderRadius: 12,
    padding: theme.spacing(0),
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.04)",
    overflow: "hidden",
  },
  tableHead: {
    backgroundColor: "var(--sidebar-color, #1e293b)",
    "& th": {
      color: "#cbd5e1",
      fontWeight: 600,
      fontSize: "0.8rem",
      textTransform: "uppercase",
      letterSpacing: "0.05em",
      borderBottom: "none",
      padding: "14px 16px",
    },
  },
  tableBody: {
    "& td": {
      padding: "12px 16px",
      fontSize: "0.875rem",
      color: "#334155",
      borderBottom: "1px solid #f1f5f9",
    },
    "& tr:hover": { backgroundColor: "#f8fafc" },
  },
  thumb: {
    width: 56, height: 40, objectFit: "cover",
    borderRadius: 6, border: "1px solid #e2e8f0",
  },
  thumbPlaceholder: {
    width: 56, height: 40, borderRadius: 6,
    background: "#f1f5f9", display: "flex",
    alignItems: "center", justifyContent: "center",
    fontSize: 10, color: "#94a3b8", fontWeight: 700,
    border: "1px solid #e2e8f0",
  },
  emptyState: {
    display: "flex", flexDirection: "column",
    alignItems: "center", justifyContent: "center",
    padding: "48px 24px", color: "#999",
  },
}));

const AppMarketplaceAdmin = () => {
  const classes = useStyles();
  const theme = useTheme();
  const sidebarColor = theme.palette.primary.main || "#1e293b";

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, target: null });

  // ── Categorias ──────────────────────────────────────────────
  const [catDialog, setCatDialog] = useState(false);
  const [categories, setCategories] = useState([]);
  const [newCatName, setNewCatName] = useState("");
  const [savingCat, setSavingCat] = useState(false);
  const [deletingCat, setDeletingCat] = useState(null);
  const [editingCat, setEditingCat] = useState(null);
  const [editCatName, setEditCatName] = useState("");

  const loadItems = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await listMarketplaceAdmin();
      setItems(data);
    } catch {
      toast.error("Erro ao carregar itens do marketplace");
    } finally {
      setLoading(false);
    }
  }, []);

  const loadCategories = useCallback(async () => {
    try {
      const { data } = await listCategories();
      setCategories(data);
    } catch {}
  }, []);

  useEffect(() => { loadItems(); }, [loadItems]);
  useEffect(() => { if (catDialog) loadCategories(); }, [catDialog, loadCategories]);

  const filtered = items.filter((i) =>
    !search.trim() ||
    i.title.toLowerCase().includes(search.toLowerCase()) ||
    (i.category || "").toLowerCase().includes(search.toLowerCase())
  );

  // ── CRUD Categorias ─────────────────────────────────────────
  const handleAddCategory = async () => {
    if (!newCatName.trim()) return;
    setSavingCat(true);
    try {
      await createCategory({ name: newCatName.trim() });
      setNewCatName("");
      await loadCategories();
    } catch {
      toast.error("Erro ao criar categoria");
    } finally {
      setSavingCat(false);
    }
  };

  const handleSaveEditCat = async () => {
    if (!editCatName.trim() || !editingCat) return;
    setSavingCat(true);
    try {
      await updateCategory(editingCat.id, { name: editCatName.trim() });
      setEditingCat(null);
      setEditCatName("");
      await loadCategories();
    } catch {
      toast.error("Erro ao atualizar categoria");
    } finally {
      setSavingCat(false);
    }
  };

  const handleDeleteCat = async (id) => {
    setDeletingCat(id);
    try {
      await deleteCategory(id);
      await loadCategories();
    } catch {
      toast.error("Erro ao remover categoria");
    } finally {
      setDeletingCat(null);
    }
  };

  // ── CRUD Items ──────────────────────────────────────────────
  const handleDeleteItem = async () => {
    const { target } = deleteDialog;
    if (!target) return;
    try {
      await deleteMarketplaceItem(target.id);
      toast.success("Item removido");
      setDeleteDialog({ open: false, target: null });
      loadItems();
    } catch {
      toast.error("Erro ao remover item");
    }
  };

  return (
    <div className={classes.root} style={{ "--sidebar-color": sidebarColor }}>

      {/* Modal criar/editar item */}
      <AppMarketplaceModal
        open={modalOpen}
        onClose={() => { setModalOpen(false); setEditItem(null); }}
        item={editItem}
        onSaved={loadItems}
      />

      {/* Dialog exclusão de item */}
      <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, target: null })}>
        <DialogTitle>Confirmar exclusão</DialogTitle>
        <DialogContent>
          <Typography>
            Deseja remover <strong>"{deleteDialog.target?.title}"</strong>?
          </Typography>
          <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
            Esta ação não pode ser desfeita.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, target: null })}>Cancelar</Button>
          <Button onClick={handleDeleteItem} color="secondary" variant="contained">Remover</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Categorias */}
      <Dialog open={catDialog} onClose={() => setCatDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Gerenciar Categorias</DialogTitle>
        <DialogContent style={{ paddingTop: 8 }}>
          {/* Adicionar nova */}
          <Box display="flex" style={{ gap: 8, marginBottom: 16 }}>
            <TextField
              size="small"
              variant="outlined"
              placeholder="Nome da categoria"
              fullWidth
              value={newCatName}
              onChange={(e) => { const v = e.target.value; setNewCatName(v); }}
              onKeyDown={(e) => e.key === "Enter" && handleAddCategory()}
              disabled={savingCat}
              inputProps={{ maxLength: 100 }}
            />
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={handleAddCategory}
              disabled={savingCat || !newCatName.trim()}
              style={{ whiteSpace: "nowrap", minWidth: 100 }}
              startIcon={savingCat ? <CircularProgress size={14} color="inherit" /> : <AddIcon />}
            >
              Adicionar
            </Button>
          </Box>

          {categories.length === 0 ? (
            <Typography variant="body2" color="textSecondary" align="center" style={{ padding: "16px 0" }}>
              Nenhuma categoria criada.
            </Typography>
          ) : (
            <List dense disablePadding>
              {categories.map((cat) => (
                <ListItem key={cat.id} style={{ borderBottom: "1px solid #f0f0f0", paddingLeft: 0 }}>
                  {editingCat?.id === cat.id ? (
                    <Box display="flex" style={{ gap: 6, flex: 1, alignItems: "center" }}>
                      <TextField
                        size="small"
                        variant="outlined"
                        fullWidth
                        value={editCatName}
                        onChange={(e) => { const v = e.target.value; setEditCatName(v); }}
                        onKeyDown={(e) => e.key === "Enter" && handleSaveEditCat()}
                        autoFocus
                        inputProps={{ maxLength: 100 }}
                      />
                      <IconButton
                        size="small"
                        style={{ color: "#16a34a" }}
                        onClick={handleSaveEditCat}
                        disabled={savingCat}
                      >
                        <CheckIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => { setEditingCat(null); setEditCatName(""); }}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  ) : (
                    <>
                      <ListItemText primary={cat.name} />
                      <ListItemSecondaryAction>
                        <Tooltip title="Editar">
                          <IconButton
                            size="small"
                            style={{ color: "#1976d2" }}
                            onClick={() => { setEditingCat(cat); setEditCatName(cat.name); }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Remover">
                          <IconButton
                            size="small"
                            style={{ color: "#ef4444" }}
                            onClick={() => handleDeleteCat(cat.id)}
                            disabled={deletingCat === cat.id}
                          >
                            {deletingCat === cat.id
                              ? <CircularProgress size={14} />
                              : <DeleteIcon fontSize="small" />}
                          </IconButton>
                        </Tooltip>
                      </ListItemSecondaryAction>
                    </>
                  )}
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCatDialog(false)}>Fechar</Button>
        </DialogActions>
      </Dialog>

      {/* Header */}
      <div className={classes.pageHeader}>
        <Typography className={classes.pageTitle}>App Marketplace</Typography>
        <Typography className={classes.pageSubtitle}>
          Gerencie os aplicativos e serviços exibidos no marketplace — {filtered.length} item(ns) cadastrado(s)
        </Typography>
      </div>

      {/* Toolbar */}
      <div className={classes.toolbarRow}>
        <TextField
          placeholder="Buscar por título ou categoria..."
          variant="outlined"
          size="small"
          className={classes.searchField}
          value={search}
          onChange={(e) => { const v = e.target.value; setSearch(v); }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon style={{ color: "#999", fontSize: 18 }} />
              </InputAdornment>
            ),
          }}
        />
        <Box display="flex" style={{ gap: 10 }}>
          <Button
            variant="outlined"
            color="default"
            startIcon={<CategoryIcon />}
            onClick={() => setCatDialog(true)}
            className={classes.addButton}
          >
            Categorias
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => { setEditItem(null); setModalOpen(true); }}
            className={classes.addButton}
          >
            Novo Item
          </Button>
        </Box>
      </div>

      {/* Tabela */}
      <div className={classes.content}>
        <Table size="small">
            <TableHead className={classes.tableHead}>
              <TableRow>
                <TableCell>Capa</TableCell>
                <TableCell>Título</TableCell>
                <TableCell>Categoria</TableCell>
                <TableCell>Descrição curta</TableCell>
                <TableCell align="center">Ordem</TableCell>
                <TableCell align="center">Status</TableCell>
                <TableCell align="center">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody className={classes.tableBody}>
              {loading ? (
                <TableRowSkeleton columns={7} />
              ) : filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7}>
                    <Box className={classes.emptyState}>
                      <Typography>
                        {search
                          ? "Nenhum item encontrado para esta busca."
                          : 'Nenhum item cadastrado. Clique em "Novo Item" para começar.'}
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                filtered.map((item) => (
                  <TableRow
                    key={item.id}
                    style={{ cursor: "pointer" }}
                    onClick={() => { setEditItem(item); setModalOpen(true); }}
                  >
                    <TableCell>
                      {item.coverImage ? (
                        <img
                          src={`${backendUrl}/public/${item.coverImage}`}
                          alt={item.title}
                          className={classes.thumb}
                        />
                      ) : (
                        <div className={classes.thumbPlaceholder}>
                          {item.title.substring(0, 2).toUpperCase()}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography style={{ fontWeight: 600, color: "#1976d2" }}>
                        {item.title}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {item.category ? (
                        <Chip label={item.category} size="small" style={{ fontSize: "0.7rem" }} />
                      ) : (
                        <Typography variant="body2" color="textSecondary">—</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        style={{ maxWidth: 260, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                        title={item.shortDescription}
                      >
                        {item.shortDescription || "—"}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2" color="textSecondary">{item.sortOrder}</Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        size="small"
                        label={item.active ? "Ativo" : "Inativo"}
                        style={{
                          backgroundColor: item.active ? "#dcfce7" : "#fee2e2",
                          color: item.active ? "#166534" : "#991b1b",
                          fontWeight: 600,
                          fontSize: "0.75rem",
                        }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Box display="flex" justifyContent="center" style={{ gap: 4 }}>
                        <Tooltip title="Editar">
                          <IconButton
                            size="small"
                            style={{ color: "#1976d2" }}
                            onClick={(e) => { e.stopPropagation(); setEditItem(item); setModalOpen(true); }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Remover">
                          <IconButton
                            size="small"
                            style={{ color: "#ef4444" }}
                            onClick={(e) => { e.stopPropagation(); setDeleteDialog({ open: true, target: item }); }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
    </div>
  );
};

export default AppMarketplaceAdmin;
